import React, { useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, GraduationCap, FlaskConical, BarChart3, Award, Rocket, MonitorPlay, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";
import { SOLUTIONS_TABS } from "../mock";
import { Reveal } from "./Reveal";

const ICONS = { UserPlus, GraduationCap, FlaskConical, BarChart3, Award, Rocket, MonitorPlay, Sparkles };

const SolutionsTabs = () => {
  const [active, setActive] = useState(SOLUTIONS_TABS[2].key); // default to Practice (matches sample)
  const current = SOLUTIONS_TABS.find(t => t.key === active);
  const Illustration = ICONS[current.icon];

  return (
    <section className="py-20 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-4">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className="text-xs font-bold tracking-widest text-[#6BBE32] mb-3">ENTERPRISE LEARNING SOLUTIONS</div>
            <h1 className="text-3xl lg:text-5xl font-bold text-[#15348C] leading-[1.1]">Enterprise Training Programs for Business-Critical Skills</h1>
            <p className="mt-4 text-gray-600">Skillsgain delivers company training and employee development programs aligned with business goals, combining expert instruction, practical learning, and enterprise technology skills.</p>
          </div>
        </Reveal>

        {/* Tab pills */}
        <Reveal delay={80}>
          <div className="flex justify-center mb-10">
            <div className="inline-flex flex-wrap justify-center gap-1 bg-white p-1.5 rounded-2xl shadow-sm border border-gray-100">
              {SOLUTIONS_TABS.map(t => {
                const Icon = ICONS[t.icon];
                const isActive = t.key === active;
                return (
                  <button key={t.key} onClick={() => setActive(t.key)}
                    className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl transition-all ${
                      isActive
                        ? "bg-[#6BBE32] text-white shadow-md scale-[1.02]"
                        : "text-[#15348C] hover:text-[#6BBE32] hover:bg-[#F4F7FB]"
                    }`}>
                    <Icon size={15}/> {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        </Reveal>

        {/* Content - 2 column */}
        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          {/* Left: features */}
          <div key={current.key + "-left"} className="space-y-4">
            {current.features.map((f, i) => (
              <Reveal key={f.title} variant="left" delay={i * 70}>
                <div className="sg-card group bg-white border border-gray-100 rounded-2xl p-5 flex gap-4">
                  <div className="w-2.5 h-2.5 mt-2 rounded-full bg-[#6BBE32] shrink-0 group-hover:scale-150 transition-transform"/>
                  <div>
                    <h4 className="font-bold text-[#15348C] group-hover:text-[#6BBE32] transition-colors">{f.title}</h4>
                    <p className="text-sm text-gray-600 mt-1 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Right: dynamic visual card */}
          <Reveal variant="right" delay={120}>
            <div key={current.key + "-right"} className="relative h-full min-h-[440px] rounded-2xl border border-gray-100 bg-white overflow-hidden flex flex-col">
              <div className="relative h-[260px] lg:h-[280px] overflow-hidden">
                <img
                  src={current.image}
                  alt={current.headline}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                  data-testid={`solution-image-${current.key}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#15348C]/70 via-[#15348C]/20 to-transparent" />
                <div className="absolute top-4 left-4 w-14 h-14 rounded-2xl bg-gradient-to-br from-[#6BBE32] to-[#4F9622] flex items-center justify-center shadow-2xl shadow-[#6BBE32]/40">
                  <Illustration size={26} className="text-white" />
                </div>
              </div>
              <div className="relative p-8 text-center flex-1 flex flex-col items-center justify-center">
                <div className="absolute -top-24 -left-24 w-72 h-72 bg-[#6BBE32]/15 rounded-full blur-3xl"/>
                <div className="absolute -bottom-24 -right-24 w-72 h-72 bg-[#8BD835]/10 rounded-full blur-3xl"/>
                <div className="relative">
                  <h3 className="text-2xl lg:text-3xl font-bold text-[#15348C]">{current.headline}</h3>
                  <p className="mt-3 text-gray-600 max-w-sm mx-auto">{current.tagline}</p>
                  <div className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-gray-600">
                    <span className="flex items-center gap-1"><CheckCircle2 size={14} className="text-[#6BBE32]"/> Enterprise-grade SLAs</span>
                    <span className="flex items-center gap-1"><CheckCircle2 size={14} className="text-[#6BBE32]"/> Global delivery</span>
                    <span className="flex items-center gap-1"><CheckCircle2 size={14} className="text-[#6BBE32]"/> Outcome-led</span>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Single CTA */}
        <Reveal delay={160}>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/contact"
              className="sg-btn-glow bg-[#8BD835] hover:bg-[#6BBE32] text-white font-semibold px-7 h-12 rounded-md inline-flex items-center gap-2 shadow-lg shadow-[#8BD835]/25 transition-colors">
              Request Corporate Proposal <ArrowRight size={17}/>
            </Link>
            <Link to="/offerings/all"
              className="border-2 border-[#15348C]/15 text-[#15348C] hover:border-[#6BBE32] hover:text-[#6BBE32] font-semibold px-7 h-12 rounded-md inline-flex items-center gap-2 transition-colors">
              Explore Enterprise Solutions
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default SolutionsTabs;
